import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ValidityBadge } from './ValidityBadge';
import { ADDONS_PANEL_STATUS } from '../buildAddonsPanel';
import { formatCalendarDate } from '../../../helpers/ExpiryRule';

const HEADING_ID = 'addons-heading';

const prefersReducedMotion = () => {
	return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
};

// `rows` are already sorted for display (buildAddonsPanel). The section only exists once the pane has loaded and
// its panel is ready or failed, so a request to scroll to it is handled the moment it mounts. The request is
// one-shot: after scrolling and focusing, the section calls `onHandled` and the page clears it, so a request
// can never fire again later (clicking the icon of the machine that is already selected sends a new request
// object, which scrolls again). Scrolling alone leaves keyboard and screen-reader focus on the icon, so the
// heading takes focus afterwards. The list is laid out in full (no inner scroll), so it is not a tab stop.
export const AddonsSection = ({ status, rows, request, onHandled }) => {
	const sectionRef = useRef(null);
	const headingRef = useRef(null);

	useEffect(() => {
		if (!request) {
			return;
		}

		sectionRef.current.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
		headingRef.current.focus({ preventScroll: true });
		onHandled();
	}, [request, onHandled]);

	const renderBody = () => {
		if (status === ADDONS_PANEL_STATUS.FAILED) {
			return <p className="mb-4">Add-ons could not be loaded.</p>;
		}

		if (!rows.length) {
			return <p className="mb-4">No add-ons.</p>;
		}

		return (
			<ul className="list-unstyled mb-4" role="list" aria-labelledby={HEADING_ID}>
				{rows.map((row) => {
					return (
						<li key={row.id} className="d-flex justify-content-between">
							<p className="me-2">{row.name}</p>
							<div className="d-flex align-items-baseline">
								<ValidityBadge status={row.status} />
								<time className="ms-2" dateTime={row.calendarDate}>
									<span className="visually-hidden">Expires </span>
									{formatCalendarDate(row.calendarDate)}
								</time>
							</div>
						</li>
					);
				})}
			</ul>
		);
	};

	return (
		<div ref={sectionRef}>
			<h6 id={HEADING_ID} ref={headingRef} tabIndex={-1} className="text-muted fs-6 fw-normal">Add-ons</h6>
			{renderBody()}
		</div>
	);
};

AddonsSection.propTypes = {
	status: PropTypes.oneOf([ADDONS_PANEL_STATUS.READY, ADDONS_PANEL_STATUS.FAILED]).isRequired,
	rows: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		status: PropTypes.string.isRequired,
		calendarDate: PropTypes.string.isRequired,
	})).isRequired,
	request: PropTypes.shape({ machineId: PropTypes.string }),
	onHandled: PropTypes.func,
};

// One stable function: a new default per render would re-run the effect above.
AddonsSection.defaultProps = {
	request: null,
	onHandled: () => {},
};
